import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import type { List, Priority, Ticket, UserProfile } from '@/lib/types'
import {
  cn,
  displayListName,
  isTicketUrgent,
  labelColor,
  listDotClass,
  PRIORITY_META,
  PRIORITY_ORDER,
  priorityLabel,
  profileName,
} from '@/lib/utils'
import { Avatar } from './Avatar'
import {
  Archive,
  ArrowRightLeft,
  Flag,
  Flame,
  Eye,
  MoreHorizontal,
  Tag,
  Users,
  X,
  MessageCircle,
  ListChecks,
  Trash2,
} from 'lucide-react'

interface Props {
  ticket: Ticket
  /** All lists of the project — used by the « move to another list »
   *  picker (the card's current list is filtered out). */
  lists?: List[]
  onUpdate: (id: string, patch: Partial<Ticket>) => void | Promise<void>
  onArchive: (id: string) => void
  /** Permanently delete the card (deferred via a 15 s undo toast handled by
   *  the parent). Hidden from the menu when not provided. */
  onDelete?: (id: string) => void
  /** True when the card is currently flagged « new » for this user. */
  isNew?: boolean
  /** Manually clear the « new » flag (mark seen). Hidden if not provided. */
  onMarkSeen?: (id: string) => void
  /** Open the cross-project « Move card » dialog. Hidden from the menu
   *  if not provided. */
  onMove?: () => void
  /** Open the « Répondre » dialog (AI-suggested replies + composer).
   *  Hidden from the menu if not provided. */
  onReply?: () => void
  /** Notifies the parent TicketCard so it can lift itself above the
   *  blurred backdrop (z-index 60) while the popover is open. */
  onOpenChange?: (open: boolean) => void
  /** All user_ids who can be subscribed to this card (owner + project members). */
  subscribableUserIds: string[]
  /** Current subscribers — null = unknown / not loaded. */
  subscriberIds: Set<string> | null
  /** Full profile map for display name + avatar lookups in the picker. */
  profilesById: Record<string, UserProfile>
  /** Used to flag "you" in the picker (visual cue). */
  currentUserId: string
  /** Subscribe / unsubscribe a user — optimistic, fires the notify email
   *  on subscribe (handled upstream in App.tsx). */
  onToggleSubscriber?: (
    ticketId: string,
    userId: string,
    subscribe: boolean,
  ) => void | Promise<void>
  /** When the card is in compact mode, vertically center the `…` trigger
   *  instead of pinning it to the top corner (where it would float off the
   *  much shorter card body). */
  compact?: boolean
  /** Deduplicated list of labels used anywhere in the current project.
   *  Surfaced in the labels picker as one-click suggestion chips so the
   *  user can reapply an existing label without retyping it. */
  projectLabels?: string[]
}

const PANEL_WIDTH = 256 // matches Tailwind w-64
const PANEL_GAP = 8 // horizontal gap between card edge and panel
const VIEWPORT_PADDING = 8 // minimum margin between panel and viewport edge

/**
 * Contextual `…` menu rendered at the top-right of a TicketCard.
 *
 * Replaces the previous single-purpose Archive button. Exposes the most
 * common card-level edits (priority, labels, archive) inline so the user
 * doesn't have to open the detail modal for quick changes.
 *
 * When open, renders a fixed backdrop with `backdrop-filter: blur` over
 * the whole viewport. The active card is lifted above the backdrop (via
 * `onOpenChange` → TicketCard sets `z-index: 60`), so only the rest of
 * the board appears blurred. The popover itself sits on z-index 70 with
 * a white surface + modal shadow.
 *
 * The popover is portaled to `document.body` so it can escape the card's
 * transformed stacking context (dnd-kit applies `transform` to the card
 * root, which traps `z-index` AND `position: fixed` inside the card).
 *
 * All interactive bits stop pointer/click propagation so the drag-and-drop
 * sortable on the card never kicks in. The menu closes on outside click and
 * on Escape.
 */
export function TicketCardMenu({
  ticket,
  lists,
  onUpdate,
  onArchive,
  onDelete,
  isNew = false,
  onMarkSeen,
  onMove,
  onReply,
  onOpenChange,
  subscribableUserIds,
  subscriberIds,
  profilesById,
  currentUserId,
  onToggleSubscriber,
  compact = false,
  projectLabels = [],
}: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [labelDraft, setLabelDraft] = useState('')
  // Viewport-relative coordinates for the portaled panel. `left` is the
  // absolute X position; we anchor the panel to the right of the card by
  // default, and flip to the left side if it would overflow the viewport.
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  // Notify TicketCard so it can lift itself above the backdrop.
  useEffect(() => {
    onOpenChange?.(open)
  }, [open, onOpenChange])

  // Position the panel next to the card itself (default: right side; flips
  // to the left when there isn't enough room). We anchor on the card's rect
  // rather than the trigger so the panel hugs the card edge regardless of
  // where the `…` button sits inside it. Recomputed on scroll + resize so
  // the popover stays visually anchored.
  useLayoutEffect(() => {
    if (!open) return
    function reposition() {
      const btn = triggerRef.current
      const card = btn?.parentElement
      if (!btn || !card) return
      const r = card.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight

      // Prefer the right side. If the panel would overflow on the right but
      // there IS room on the left, flip. Otherwise (cramped both sides) keep
      // right and let the clamp below pull it back in.
      const fitsRight = r.right + PANEL_GAP + PANEL_WIDTH + VIEWPORT_PADDING <= vw
      const fitsLeft = r.left - PANEL_GAP - PANEL_WIDTH - VIEWPORT_PADDING >= 0
      const onRight = fitsRight || !fitsLeft

      let left = onRight
        ? r.right + PANEL_GAP
        : r.left - PANEL_GAP - PANEL_WIDTH
      // Final clamp — never let the panel slip off-screen horizontally.
      left = Math.max(
        VIEWPORT_PADDING,
        Math.min(left, vw - PANEL_WIDTH - VIEWPORT_PADDING),
      )

      // Vertically: align with the card's top. If a measured panel height
      // would overflow the bottom of the viewport, lift the panel up.
      let top = r.top
      const panelH = panelRef.current?.getBoundingClientRect().height
      if (panelH && top + panelH + VIEWPORT_PADDING > vh) {
        top = Math.max(VIEWPORT_PADDING, vh - panelH - VIEWPORT_PADDING)
      }

      setPanelPos({ top, left })
    }
    reposition()
    // Second pass once the panel has rendered (so we know its height for
    // bottom-overflow clamping).
    const id = requestAnimationFrame(reposition)
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      cancelAnimationFrame(id)
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open])

  // Escape closes the menu. Outside click is handled by the backdrop's own
  // click handler (the backdrop covers everything but the lifted card +
  // the popover, so any click elsewhere lands on it).
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Auto-close on mouse-leave (desktop hover-out → dismiss). A 15 % safety
  // margin around the popover prevents an over-eager close when the cursor
  // grazes the edge — so the user has to make a clear move OUT of the area.
  //
  // We don't activate the close-on-leave logic until the cursor has entered
  // the popover at least once. Otherwise the popover would slam shut
  // immediately when it opens (the cursor starts on the trigger button,
  // outside the popover's safety zone).
  useEffect(() => {
    if (!open || !panelPos) return
    let hasEntered = false
    const BUFFER_PCT = 0.15

    function onMove(e: MouseEvent) {
      const panel = panelRef.current?.getBoundingClientRect()
      if (!panel) return
      const inside =
        e.clientX >= panel.left &&
        e.clientX <= panel.right &&
        e.clientY >= panel.top &&
        e.clientY <= panel.bottom
      if (!hasEntered) {
        if (inside) hasEntered = true
        return
      }
      // Past the first entry — leaving the inflated safety zone closes.
      const bx = panel.width * BUFFER_PCT
      const by = panel.height * BUFFER_PCT
      const inSafety =
        e.clientX >= panel.left - bx &&
        e.clientX <= panel.right + bx &&
        e.clientY >= panel.top - by &&
        e.clientY <= panel.bottom + by
      if (!inSafety) setOpen(false)
    }
    document.addEventListener('mousemove', onMove)
    return () => document.removeEventListener('mousemove', onMove)
  }, [open, panelPos])

  function setPriority(p: Priority | null) {
    if (p === (ticket.priority ?? null)) return
    onUpdate(ticket.id, { priority: p })
  }

  function addLabel() {
    const v = labelDraft.trim()
    setLabelDraft('')
    if (!v) return
    const existing = ticket.labels ?? []
    if (existing.some((l) => l.toLowerCase() === v.toLowerCase())) return
    onUpdate(ticket.id, { labels: [...existing, v] })
  }

  function removeLabel(label: string) {
    onUpdate(ticket.id, {
      labels: (ticket.labels ?? []).filter((l) => l !== label),
    })
  }

  function archive() {
    setOpen(false)
    onArchive(ticket.id)
  }

  function remove() {
    setOpen(false)
    onDelete?.(ticket.id)
  }

  /** Move the card to another list of the SAME project. Mirrors the detail
   *  modal's changeList: stamps/clears done_at when crossing the « done »
   *  boundary so resolved metrics stay correct. */
  function changeList(targetId: string) {
    if (targetId === ticket.list_id) return
    const fromDone = lists?.find((l) => l.id === ticket.list_id)?.is_done ?? false
    const toDone = lists?.find((l) => l.id === targetId)?.is_done ?? false
    const patch: Partial<Ticket> = { list_id: targetId }
    if (toDone && !fromDone) patch.done_at = new Date().toISOString()
    if (!toDone && fromDone) patch.done_at = null
    onUpdate(ticket.id, patch)
    setOpen(false)
  }

  const otherLists = (lists ?? []).filter((l) => l.id !== ticket.list_id)

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={t('cardMenu.options')}
        title={t('cardMenu.options')}
        aria-haspopup="menu"
        aria-expanded={open}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        className={cn(
          'absolute right-1.5 z-10 rounded-lg border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-1 text-[var(--color-text-dim)] shadow-[var(--shadow-soft)] transition-all hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]',
          // Compact rows are too short to pin the trigger to the top corner,
          // so we vertically centre it. Detail-mode keeps the top-corner pin.
          compact ? 'top-1/2 -translate-y-1/2' : 'top-1.5',
          // Hidden until card hover, but stays visible while the menu is open
          // so the user can interact with the popover.
          open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
        )}
      >
        <MoreHorizontal className="h-3.5 w-3.5" />
      </button>

      {open &&
        panelPos &&
        createPortal(
          <>
            {/* Backdrop — blurs the rest of the board. The active card is
                lifted to z-60 by TicketCard so it stays sharp. Click on the
                backdrop closes the menu. */}
            <div
              role="presentation"
              onPointerDown={(e) => {
                e.stopPropagation()
                setOpen(false)
              }}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 50,
                backdropFilter: 'blur(3px)',
                WebkitBackdropFilter: 'blur(3px)',
              }}
              className="bg-black/5 transition-opacity duration-150"
            />

            {/* Popover — white surface, modal shadow, above the backdrop AND
                above the lifted card. */}
            <div
              ref={panelRef}
              role="menu"
              style={{
                position: 'fixed',
                top: panelPos.top,
                left: panelPos.left,
                width: PANEL_WIDTH,
                zIndex: 70,
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-[12px] shadow-[var(--shadow-modal)]"
            >
              {/* Members — FIRST section so the user can quickly subscribe /
                  unsubscribe folks without opening the modal. Each row is a
                  toggle; subscribing fires the email notification upstream. */}
              {onToggleSubscriber && subscribableUserIds.length > 0 && (
                <>
                  <div className="mb-2 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
                    <Users className="h-3 w-3" />
                    {t('topbar.members')}
                  </div>
                  <div className="mb-3 max-h-44 space-y-0.5 overflow-y-auto pr-0.5">
                    {subscribableUserIds.map((uid) => {
                      const profile = profilesById[uid]
                      const isSub = subscriberIds?.has(uid) ?? false
                      const isMe = uid === currentUserId
                      const name = profileName(profile) || (isMe ? t('common.you') : t('topbar.members'))
                      return (
                        <button
                          key={uid}
                          type="button"
                          onClick={() => onToggleSubscriber(ticket.id, uid, !isSub)}
                          className={cn(
                            'flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors',
                            isSub
                              ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                              : 'hover:bg-[var(--color-surface-2)]',
                          )}
                        >
                          <Avatar
                            profile={profile}
                            userId={uid}
                            className="h-5 w-5"
                            textClassName="text-[8px]"
                          />
                          <span className="min-w-0 flex-1 truncate text-[11.5px]">
                            {name}
                            {isMe && (
                              <span className="ml-1 text-[10px] text-[var(--color-text-dim)]">
                                ({t('common.you').toLowerCase()})
                              </span>
                            )}
                          </span>
                          <span
                            className={cn(
                              'shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ring-1 ring-inset',
                              isSub
                                ? 'bg-[var(--color-accent)] text-white ring-[var(--color-accent)]'
                                : 'text-[var(--color-text-dim)] ring-[var(--color-border-soft)]',
                            )}
                          >
                            {isSub ? t('cardMenu.subscribers') : t('cardMenu.inviteToFollow')}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </>
              )}

              {/* Priority */}
              <div className="mb-2 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
                <Flag className="h-3 w-3" />
                {t('cardMenu.priority')}
              </div>
              <div className="mb-3 flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() => setPriority(null)}
                  className={cn(
                    'rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset transition-colors',
                    ticket.priority == null
                      ? 'bg-[var(--color-surface-3)] text-[var(--color-text-strong)] ring-[var(--color-border)]'
                      : 'bg-[var(--color-surface)] text-[var(--color-text-dim)] ring-[var(--color-border-soft)] hover:bg-[var(--color-surface-2)]',
                  )}
                >
                  {t('cardMenu.priorityNone')}
                </button>
                {PRIORITY_ORDER.map((p) => {
                  const meta = PRIORITY_META[p]
                  const active = ticket.priority === p
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPriority(p)}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset transition-colors',
                        active
                          ? meta.chip
                          : 'bg-[var(--color-surface)] text-[var(--color-text-dim)] ring-[var(--color-border-soft)] hover:bg-[var(--color-surface-2)]',
                      )}
                    >
                      <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
                      {priorityLabel(p)}
                    </button>
                  )
                })}
              </div>

              {/* Labels */}
              <div className="mb-2 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
                <Tag className="h-3 w-3" />
                {t('cardMenu.labels')}
              </div>
              <div className="mb-3 flex flex-wrap items-center gap-1">
                {ticket.labels.map((label) => (
                  <span
                    key={label}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] font-medium ring-1 ring-inset',
                      labelColor(label),
                    )}
                  >
                    {label}
                    <button
                      type="button"
                      onClick={() => removeLabel(label)}
                      className="rounded-full p-0.5 transition-colors hover:bg-black/10"
                      aria-label={`${t('common.delete')} ${label}`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
                <input
                  value={labelDraft}
                  onChange={(e) => setLabelDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addLabel()
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      setLabelDraft('')
                      setOpen(false)
                    }
                  }}
                  onBlur={addLabel}
                  placeholder={t('cardMenu.labelAddPlaceholder')}
                  className="min-w-[90px] flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-1.5 py-0.5 text-[11px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
                />
              </div>

              {/* Existing labels from the project — one click to reapply.
                  Filtered against what's already on this card so the user
                  isn't tempted to re-add a duplicate. */}
              {(() => {
                const cur = new Set(ticket.labels ?? [])
                const suggestions = (projectLabels ?? []).filter(
                  (l) => !cur.has(l),
                )
                if (suggestions.length === 0) return null
                return (
                  <div className="mb-3 -mt-1">
                    <div className="mb-1 text-[10px] text-[var(--color-text-dim)]">
                      {t('cardMenu.labelsExisting')}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {suggestions.map((label) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() =>
                            onUpdate(ticket.id, {
                              labels: [...(ticket.labels ?? []), label],
                            })
                          }
                          title={t('cardMenu.labelsReapply', { label })}
                          aria-label={t('cardMenu.labelsReapply', { label })}
                          className={cn(
                            'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] font-medium opacity-70 ring-1 ring-inset transition-opacity hover:opacity-100',
                            labelColor(label),
                          )}
                        >
                          + {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )
              })()}

              {/* Move to another list of the SAME project */}
              {otherLists.length > 0 && (
                <>
                  <div className="mb-2 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
                    <ListChecks className="h-3 w-3" />
                    {t('cardMenu.moveToList')}
                  </div>
                  <div className="mb-3 flex flex-wrap gap-1">
                    {otherLists.map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => changeList(l.id)}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-surface)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-text)] ring-1 ring-inset ring-[var(--color-border-soft)] transition-colors hover:bg-[var(--color-surface-2)] hover:ring-[var(--color-accent)]"
                      >
                        <span className={cn('h-1.5 w-1.5 rounded-full', listDotClass(l))} />
                        {displayListName(l)}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Divider + reply + cross-project move + archive */}
              <div className="-mx-3 mt-1 border-t border-[var(--color-border-soft)]" />
              {isNew && onMarkSeen && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false)
                    onMarkSeen(ticket.id)
                  }}
                  className="mt-2 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-link)]"
                >
                  <Eye className="h-3.5 w-3.5" />
                  {t('cardMenu.markSeen')}
                </button>
              )}
              {isTicketUrgent(ticket) && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false)
                    onUpdate(ticket.id, {
                      ai_is_urgent: false,
                      ...(ticket.priority === 'urgent' ? { priority: null } : {}),
                    })
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-link)]',
                    isNew && onMarkSeen ? 'mt-0.5' : 'mt-2',
                  )}
                >
                  <Flame className="h-3.5 w-3.5" />
                  {t('cardMenu.clearUrgent')}
                </button>
              )}
              {onReply && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false)
                    onReply()
                  }}
                  className="mt-2 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-link)]"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                  {t('cardMenu.reply')}
                </button>
              )}
              {onMove && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false)
                    onMove()
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-link)]',
                    onReply ? 'mt-0.5' : 'mt-2',
                  )}
                >
                  <ArrowRightLeft className="h-3.5 w-3.5" />
                  {t('cardMenu.changeChannel')}
                </button>
              )}
              <button
                type="button"
                onClick={archive}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-danger)]',
                  onMove || onReply ? 'mt-0.5' : 'mt-2',
                )}
              >
                <Archive className="h-3.5 w-3.5" />
                {t('cardMenu.archive')}
              </button>
              {onDelete && (
                <button
                  type="button"
                  onClick={remove}
                  className="mt-0.5 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-[var(--color-danger)] transition-colors hover:bg-[var(--color-danger-bg)]"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t('cardMenu.delete')}
                </button>
              )}
            </div>
          </>,
          document.body,
        )}
    </>
  )
}
